import {
  Popover,
  Paper,
  Typography,
  Box,
  Avatar,
  Divider,
  IconButton,
  Chip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function formatLastLogin(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function ProfileDialog({ userDetails, isOpen, onClose, lastLoginTime, anchorElement }) {
  const theme = useTheme();
  const { palette } = theme;

  const displayName = userDetails?.userDisplayName || 'Guest';
  const initial = displayName.charAt(0).toUpperCase();
  const status = (userDetails?.userStatus || '').toLowerCase();
  const isActive = status === 'active';

  const infoRows = [
    { label: 'Username', value: displayName },
    { label: 'Mobile', value: userDetails?.userPhoneNo || '—' },
    { label: 'Email', value: userDetails?.userMailId || 'Not provided' },
    { label: 'Role', value: userDetails?.roleName || '—' },
  ];

  return (
    <Popover
      open={isOpen}
      onClose={onClose}
      anchorEl={anchorElement}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          elevation: 8,
          sx: {
            mt: 0.5,
            minWidth: 260,
            maxWidth: 280,
            width: 260,
            borderRadius: 1.5,
            overflow: 'visible',
            border: '1px solid',
            borderColor: 'divider',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -5,
              right: 16,
              width: 10,
              height: 10,
              bgcolor: palette.primary.main,
              borderLeft: '1px solid',
              borderTop: '1px solid',
              borderColor: 'divider',
              transform: 'rotate(45deg)',
            },
          },
        },
      }}
    >
      <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1.5 }}>
        {/* Header */}
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${alpha(palette.primary.dark, 0.9)} 100%)`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Profile
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Avatar + name + status */}
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: alpha(palette.primary.main, 0.2),
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '1.1rem',
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {displayName}
              </Typography>
              <Chip
                size="small"
                label={userDetails?.userStatus || 'N/A'}
                color={isActive ? 'success' : 'error'}
                variant={isActive ? 'filled' : 'outlined'}
                sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.7rem' }}
              />
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 0.75 }} />

        {/* Info rows */}
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Stack spacing={1.25}>
            {infoRows.map(({ label, value }) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }} color="text.primary">
                  {value}
                </Typography>
              </Box>
            ))}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Last Login
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }} color="text.primary">
                {formatLastLogin(lastLoginTime)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Popover>
  );
}

export default ProfileDialog;
